import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    effect,
    EventEmitter,
    input,
    model,
    Output,
    signal,
    ViewEncapsulation,
    WritableSignal,
} from "@angular/core";
import { S25Util } from "../../../util/s25-util";
import { Doc } from "./s25.document.const";
import { InferSignal, WritableSignals } from "../../../pojo/Util";
import { BpeVariableData } from "../../bpe/bpe.vars.component";
import { DocumentUtil } from "./s25.document.util";
import { ContactRole } from "../../../services/role.service";
import { CustomAttribute } from "../../../services/custom.attribute.service";

@Component({
    standalone: false,
    selector: "s25-ng-document-report-builder-table",
    template: `
        <div class="rows" [class.resizing]="resizing()" [attr.editable]="editable()">
            @if (editable()) {
                <div #controls class="controls first">
                    <button class="addRow dotControl right" (click)="addRow(0)">
                        <s25-ng-icon [type]="'plus'" [label]="'Add Row'" />
                    </button>
                </div>
            }
            @for (row of rows(); track $index) {
                <div class="row" [class.last]="$last">
                    <div class="cells" [style.--cells]="row.cells.length">
                        @if (editable()) {
                            <div #controls class="controls first">
                                <button class="addCell dotControl" (click)="addCell(row, 0)">
                                    <s25-ng-icon [type]="'plus'" />
                                </button>
                            </div>
                        }
                        @for (cell of row.cells; track $index) {
                            <div class="cell" [class.invalid]="!cell.valid()" [style.--width]="cell.width()">
                                <s25-ng-document-report-builder-cell
                                    [documentType]="type()"
                                    [cell]="cell"
                                    [class.last]="$last"
                                    [editable]="editable()"
                                    (changed)="changed.emit()"
                                    (widthChange)="onCellWidthChange(row, $index, $event)"
                                />

                                @if (editable()) {
                                    <div class="controls">
                                        @if (row.cells.length > 1 || rows().length > 1 || allowEmpty()) {
                                            <button class="deleteCell dotControl" (click)="deleteCell(row, $index)">
                                                <s25-ng-icon [type]="'minus'" [label]="'Delete Cell'" />
                                            </button>
                                        }
                                    </div>
                                }
                            </div>

                            <div #controls class="controls" [class.last]="$last">
                                @if (editable()) {
                                    @if (!$last) {
                                        <s25-ng-icon
                                            [tabindex]="0"
                                            class="resizeHandle"
                                            [type]="'grip'"
                                            [label]="'Resize'"
                                            (mousedown)="startCellResize($event, row, $index, controls, 'mouse')"
                                            (keydown.enter)="startCellResize($event, row, $index, controls, 'keyboard')"
                                        />
                                    }
                                    <button class="addCell dotControl" (click)="addCell(row, $index + 1)">
                                        <s25-ng-icon [type]="'plus'" [label]="'Add Cell'" />
                                    </button>
                                }
                            </div>
                        }
                    </div>

                    @if (editable()) {
                        <div #controls class="controls">
                            @if (rows().length > 1 || allowEmpty()) {
                                <button class="deleteRow dotControl right" (click)="deleteRow($index)">
                                    <s25-ng-icon [type]="'minus'" [label]="'Delete Row'" />
                                </button>
                            }
                        </div>
                    }
                </div>

                <div #controls class="controls" [class.last]="$last">
                    @if (editable()) {
                        <button class="addRow dotControl right" (click)="addRow($index + 1)">
                            <s25-ng-icon [type]="'plus'" [label]="'Add Row'" />
                        </button>
                    }
                </div>
            }
        </div>
    `,
    styles: `
        /* Revert any global styles... */
        * {
            all: revert;
            box-sizing: border-box;
        }

        :host {
            --border-width: 1px;
            --border-color: #ccc;
            --border-radius: 3px;
            --resize-handle-width: 5px;
            --resize-handle-color: #f2f2f2;
            --control-bg: #f2f2f2;
            --green: #60a725;
            --red: #d62000;
            --red-alpha: color-mix(in lch, var(--red), #fff0 85%);
            --cell-highlight: color-mix(in lch, var(--cn-blue), #fff0 85%);
            --resized-width: initial;
        }

        ::ng-deep body.nm-party--on s25-ng-document-report-builder-table {
            --border-color: #fff3;
            --resize-handle-color: #5e5e5e;
        }

        .rows {
            border: var(--border-width) solid var(--border-color);
            border-radius: var(--border-radius);
        }

        .row {
            position: relative;
        }

        .cells {
            position: relative;
            display: flex;
            min-height: 2em;
        }

        .cell {
            position: relative;
            width: calc(1% * var(--resized-width, var(--width)));
            flex-grow: 0;
            flex-shrink: 0;
        }

        .cell.last {
            flex-grow: 1;
        }

        .rows[editable="true"]:not(.resizing) > .row > .cells > .cell:hover,
        .rows[editable="true"] > .row > .cells > .cell:has(> s25-ng-document-report-builder-cell:focus-within) {
            background-color: var(--cell-highlight);
            cursor: pointer;
            box-shadow: 0 0 0 var(--border-width) var(--cn-blue);
        }

        .cell.invalid {
            box-shadow: 0 0 0 var(--border-width) var(--red);
        }

        .controls {
            z-index: 1;
            flex: 0;
            position: relative;
        }

        .controls::before {
            content: "";
            width: var(--border-width);
            background-color: var(--border-color);
            position: absolute;
            height: 100%;
            left: calc(var(--border-width) / -2);
        }

        .controls.last {
            translate: var(--border-width) 0;
        }

        .controls.first::before,
        .controls.last::before {
            background: transparent;
        }

        .rows > .controls::before {
            width: 100%;
            height: var(--border-width);
        }

        .cell > .controls {
            position: absolute;
            left: 50%;
            top: 0;
        }

        .rows[editable="true"]:not(.resizing) > .row > .controls:hover::before,
        .rows[editable="true"]:not(.resizing) > .row > .controls:has(:focus-visible)::before,
        .rows[editable="true"]:not(.resizing) > .row > .cells > .controls:hover::before,
        .rows[editable="true"]:not(.resizing) > .row > .cells > .controls:has(:focus-visible)::before,
        .controls.active::before {
            background-color: var(--cn-blue);
        }

        .dotControl {
            --offset: 0.25rem;
            --size: 0.4rem;
            --transition-duration: 0.1s;
            position: absolute;
            translate: -50% calc(-100% - var(--offset));
            width: var(--size);
            height: var(--size);
            border-radius: 50%;
            opacity: 0;
            transition:
                opacity var(--transition-duration) ease-in-out,
                width var(--transition-duration) ease-in-out,
                height var(--transition-duration) ease-in-out;
            padding: 0;
            border: 0;
            cursor: pointer;
            background-color: var(--dot-color);
        }

        .dotControl:hover,
        .dotControl:focus-visible {
            --size: 1em;
        }

        .rows[editable="true"]:where(.resizing, :has(.dotControl:hover)) .dotControl:not(:hover) {
            opacity: 0 !important;
        }

        .rows[editable="true"] .controls:has(+ .row:hover) .dotControl,
        .rows[editable="true"] .row:hover > .controls .dotControl,
        .rows[editable="true"] .row:hover + .controls .dotControl,
        .rows[editable="true"] .controls:has(+ .cell:hover) .dotControl,
        .rows[editable="true"] .cell:hover .controls .dotControl,
        .rows[editable="true"] .cell:hover + .controls .dotControl,
        .rows[editable="true"] .dotControl:hover {
            opacity: 1;
        }

        .rows[editable="true"] .controls:has(+ .row :focus-visible) .dotControl,
        .rows[editable="true"] .row:has(:focus-visible) > .controls .dotControl,
        .rows[editable="true"] .row:has(:focus-visible) + .controls .dotControl,
        .rows[editable="true"] .controls:has(+ .cell:focus-within) .dotControl,
        .rows[editable="true"] .cell:focus-within .controls .dotControl,
        .rows[editable="true"] .cell:focus-within + .controls .dotControl,
        .rows[editable="true"] .dotControl:focus-within {
            opacity: 1;
        }

        .dotControl:hover s25-ng-icon,
        .dotControl:focus-visible s25-ng-icon {
            display: block;
        }

        .dotControl s25-ng-icon {
            display: none;
            top: 50%;
            left: 50%;
            translate: -50% -50%;
            font-size: 0.6rem;
            color: white;
            position: absolute;
        }

        .row > .controls {
            position: absolute;
            right: calc(var(--border-width) / -2);
            top: 50%;
        }

        .dotControl.right {
            translate: var(--offset) -50%;
            left: 100%;
            top: calc(-1 * var(--border-width));
        }

        .rows:not(.resizing) .dotControl.right:hover::before {
            width: var(--offset);
            height: var(--border-width);
            left: calc(-1 * var(--offset));
            bottom: calc(50% - var(--border-width) / 2);
        }

        .rows:not(.resizing) .resizeHandle:hover,
        .resizing .controls.active .resizeHandle {
            background-color: var(--cn-blue);
            color: white;
        }

        .resizeHandle {
            background: var(--resize-handle-color);
            padding-block: 0.15rem;
            border-radius: var(--border-radius);
            position: absolute;
            top: 50%;
            left: 50%;
            translate: -50% -50%;
            font-size: 0.5em;
            cursor: ew-resize;
        }

        .resizing {
            cursor: ew-resize;
        }

        .addCell,
        .addRow {
            --dot-color: var(--green);
        }

        .controls:has(:is(.addCell, .addRow):hover)::before,
        .controls:has(:is(.addCell, .addRow):focus-within)::before {
            background-color: var(--green) !important;
        }

        .rows[editable="true"]:not(.resizing, :has(.dotControl:hover)) .controls:has(+ .cell:hover) .addCell {
            opacity: 1;
        }

        .deleteCell,
        .deleteRow {
            --dot-color: var(--red);
        }

        .row:has(.deleteRow:hover),
        .cell:has(.deleteCell:hover),
        .row:has(.deleteRow:focus-visible),
        .cell:has(.deleteCell:focus-visible) {
            background-color: var(--red-alpha) !important;
            box-shadow: 0 0 0 var(--border-width) var(--red) !important;
        }

        /* Nested tables */
        .rows .rows {
            border: none;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentReportBuilderTableComponent {
    type = input.required<Doc.SubreportTypeId>();
    section = input.required<InferSignal<ReportBuilderCell["section"]>>();
    rows = model.required<ReportBuilderRow[]>();
    breakpoints = input<number[]>([]);
    allowEmpty = input<boolean>(false);
    editable = input<boolean>(true);

    @Output() changed = new EventEmitter<void>();

    minCellWidth = 1; // Percent
    snapDistance = 1; // Percent
    resizing = signal(false);

    protected readonly Doc = Doc;

    constructor(private cd: ChangeDetectorRef) {
        effect(() => {
            for (const row of this.rows()) this.normalizeCellWidths(row);
        });
    }

    startCellResize(
        event: MouseEvent,
        row: ReportBuilderRow,
        cellIndex: number,
        controlsElem: HTMLElement,
        type: "mouse" | "keyboard",
    ) {
        const { cd, minCellWidth, resizing, changed } = this;
        if (resizing()) return;

        resizing.set(true);
        const mouseInitX = event?.pageX;
        let keyboardOffset = 0;
        const keyboardStep = 1; // Percent
        const snapDistance = type === "mouse" ? this.snapDistance : keyboardStep / 2;
        const rowElem = (event.target as HTMLElement).closest(".row") as HTMLElement;
        const cellElems = Array.from(rowElem.querySelectorAll("& > .cells > .cell")) as HTMLElement[];

        controlsElem.classList.add("active");

        let breakpoints = DocumentUtil.getBreakpoints(this.rows().filter((r) => r !== row));
        breakpoints = S25Util.array.unique(breakpoints.concat(this.breakpoints() ?? []));
        breakpoints.sort((a, b) => a - b);

        function resizeCellsMouse(event: MouseEvent) {
            if (!resizing()) return;
            const widths = getNewWidths(((event.pageX - mouseInitX) / rowElem.offsetWidth) * 100);
            resizeCells(widths);
        }

        function stopResizeMouse(event: MouseEvent) {
            if (!resizing()) return;
            const widths = getNewWidths(((event.pageX - mouseInitX) / rowElem.offsetWidth) * 100);
            stop(widths);
        }

        function onKeyboard(event: KeyboardEvent) {
            if (!resizing()) return;
            if (event.key === "Escape") return stop();

            if (event.key === "ArrowLeft") keyboardOffset -= keyboardStep;
            else if (event.key === "ArrowRight") keyboardOffset += keyboardStep;

            const widths = getNewWidths(keyboardOffset);
            if (event.key === "Tab" || event.key === "Enter") stop(widths);
            else resizeCells(widths);
        }

        function getNewWidths(change: number) {
            const widths = row.cells.map((cell) => cell.width());
            const options = { minCellWidth, breakpoints, snapDistance };
            return DocumentUtil.reportBuilderResizeCells(widths, cellIndex, change, options);
        }

        function resizeCells(widths: number[]) {
            for (const [i, elem] of S25Util.array.enumerate(cellElems)) {
                elem.style.setProperty("--resized-width", `${widths[i]}`);
            }
        }

        function stop(widths?: number[]) {
            window.removeEventListener("mousemove", resizeCellsMouse);
            window.removeEventListener("mouseup", stopResizeMouse);
            window.removeEventListener("keydown", onKeyboard);

            if (widths) {
                for (const [i, cell] of S25Util.array.enumerate(row.cells)) cell.width.set(widths[i]);
            }

            controlsElem.classList.remove("active");
            for (const elem of cellElems) {
                elem.style.removeProperty("--resized-width");
            }

            resizing.set(false);
            cd.detectChanges();
            changed.emit();
        }

        if (type === "mouse") {
            window.addEventListener("mousemove", resizeCellsMouse);
            window.addEventListener("mouseup", stopResizeMouse);
        } else if (type === "keyboard") {
            // Timeout to avoid catching the triggering event as it bubbles
            setTimeout(() => window.addEventListener("keydown", onKeyboard));
        }
    }

    addCell(row: ReportBuilderRow, index: number) {
        if (row.cells.length + 1 > 100 / this.minCellWidth) {
            const width = 100 / (row.cells.length + 1);
            for (const cell of row.cells) cell.width.set(width);
            const newCell = DocumentUtil.newCell({ section: this.section(), width });
            row.cells.splice(index, 0, newCell);
            return;
        }

        const left = row.cells[index - 1]?.width() ?? 0;
        const right = row.cells[index]?.width() ?? 0;
        const targetWidth = Math.max(this.minCellWidth, (index < row.cells.length ? right : left) / 2);
        const dir = index < row.cells.length ? 1 : -1;
        let i = dir < 0 ? index + dir : index + dir - 1;
        let remaining = targetWidth;
        while (i >= 0 && i < row.cells.length && remaining > 0) {
            const adjustment = Math.min(Math.max(0, row.cells[i].width() - this.minCellWidth), remaining);
            row.cells[i].width.update((width) => width - adjustment);
            remaining -= adjustment;
            i += dir;
        }

        const newCell = DocumentUtil.newCell({
            section: this.section(),
            width: targetWidth - remaining,
        });
        row.cells.splice(index, 0, newCell);
        this.cd.detectChanges();
        this.changed.emit();
    }

    deleteCell(row: ReportBuilderRow, index: number) {
        if (row.cells.length === 1) return this.deleteRow(this.rows().indexOf(row));

        if (index === row.cells.length - 1) {
            row.cells[index - 1].width.update((val) => val + row.cells[index].width());
        } else {
            row.cells[index + 1].width.update((val) => val + row.cells[index].width());
        }
        row.cells.splice(index, 1);
        this.cd.detectChanges();
        this.changed.emit();
    }

    addRow(index: number) {
        this.rows().splice(index, 0, {
            cells: [DocumentUtil.newCell({ section: this.section() })],
        });
        this.cd.detectChanges();
        this.changed.emit();
    }

    deleteRow(index: number) {
        if (this.rows().length === 1 && !this.allowEmpty()) return;
        this.rows().splice(index, 1);
        this.cd.detectChanges();
        this.changed.emit();
    }

    onCellWidthChange(row: ReportBuilderRow, index: number, width: number) {
        let widths = row.cells.map((cell) => cell.width());
        const change = width - widths[index];
        widths = DocumentUtil.reportBuilderResizeCells(widths, index, change, { minCellWidth: this.minCellWidth });
        for (const [i, cell] of S25Util.array.enumerate(row.cells)) cell.width.set(widths[i]);
    }

    // Normalize cell widths so that they sum to 100
    normalizeCellWidths(row: ReportBuilderRow) {
        const defaultCellWidth = 100 / row.cells.length;
        const total = S25Util.array.count(row.cells, (cell) => cell.width() || defaultCellWidth);
        const normalized = S25Util.equals(total, 100, 0.01) && row.cells.every((cell) => cell.width());
        if (normalized) return;
        for (const cell of row.cells) {
            cell.width.update((width) => ((width || defaultCellWidth) / total) * 100);
        }
    }
}

export type ReportBuilder = {
    variable: string;
    header: ReportBuilderRow[];
    body: ReportBuilderRow[];
};

export type ReportBuilderRow = {
    cells: ReportBuilderCell[];
};

export type ReportBuilderCell = WritableSignals<{
    section: "header" | "body";
    variable: ReportBuilderCellVariable;
    display: ReportBuilderCellDisplay[];
    label: string;
    css: string;
    width: number;
    valid: boolean;
}>;

export type ReportBuilderCellDisplay = {
    type: InferSignal<ReportBuilderCellVariable["type"]> | "text" | "functionText";
    value: string;
};

export type ReportBuilderCellVariable = {
    type: WritableSignal<"variable" | "function" | "subreport">;
    state: BpeVariableData;
    arguments: WritableSignal<ReportBuilderCellFunctionArgument[]>;
    subreportId: number;
    subreport: ReportBuilder;
};

export type ReportBuilderCellFunctionArgument = {
    type: Doc.ReportBuilderFunctionArgumentType;
    state?: BpeVariableData;
    text?: string;
    error: WritableSignal<string | undefined>;
};

export type ReportBuilderParserData = {
    eventRole: Map<number, ContactRole>;
    eventAttribute: Map<number, CustomAttribute>;
};
